import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-daisyui';
import { LuArrowRightLeft, LuBookOpenText, LuBot } from 'react-icons/lu';
import { Link, useParams } from 'react-router-dom';

import { StandaloneProject } from '../../entities/standalone-project';
import { StandaloneTask } from '../../entities/standalone-task';
import { getStandaloneProjectAnalytics } from '../../shared/api/dashboard';
import { getStandaloneProject, listStandaloneTasks } from '../../shared/api/standalone';
import { useAIChat } from '../../shared/contexts/AIChat.context';
import { useSetDashboardHeader } from '../../shared/contexts/DashboardHeader.context';
import { useDateRange } from '../../shared/contexts/DateRange.provider';
import { useProjectById } from '../../shared/contexts/Projects.provider';
import { useAsyncStat } from '../../shared/hooks/use-async-stat';
import { asISODate, fmt1, fmtDays, fmtPct, psiBadge, safeNum, trendArrow } from '../../shared/utils/stat-helpers';
import { InfoTip, KpiCard, PsiGauge, StatEmpty, StatError, StatLoading } from '../../widgets/stats';
import TransferModal from '../../widgets/transfer/TransferModal';
import { ActionsMenu } from '../../widgets/ui/ActionsMenu';

export function StandaloneProjectStatsPage() {
  const { projectId } = useParams();
  const project = useProjectById(projectId);
  const dateRangeCtx = useDateRange();
  const setDashHeader = useSetDashboardHeader();
  const { openChat } = useAIChat();
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  // Board state (statuses + tasks) — used only for the status/priority distribution charts and
  // the forecast's "remaining open" count. The flow-summary endpoint itself has no per-task list.
  const [board, setBoard] = useState<StandaloneProject | null>(null);
  const [tasks, setTasks] = useState<StandaloneTask[]>([]);
  const [boardLoading, setBoardLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setBoardLoading(true);
    Promise.all([getStandaloneProject(projectId), listStandaloneTasks(projectId)])
      .then(([proj, taskList]) => {
        if (cancelled) return;
        setBoard(proj);
        setTasks(taskList);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setBoardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const {
    data: stat,
    loading,
    error,
  } = useAsyncStat(
    async () => {
      if (!projectId) return null;
      const range =
        dateRangeCtx?.from && dateRangeCtx?.to
          ? { from: asISODate(dateRangeCtx.from), to: asISODate(dateRangeCtx.to) }
          : undefined;
      const { stat } = await getStandaloneProjectAnalytics(projectId, range);
      return stat;
    },
    [projectId, dateRangeCtx?.from, dateRangeCtx?.to],
    'Failed to load project analytics.',
  );

  const current = stat?.current;
  const previous = stat?.previous;
  const psi = safeNum(stat?.psi, 0);

  const avgCycleDays = current?.avgCycleTimeHours != null ? current.avgCycleTimeHours / 24 : null;
  const prevAvgCycleDays = previous?.avgCycleTimeHours != null ? previous.avgCycleTimeHours / 24 : null;

  const throughputDelta = safeNum(current?.completedCount) - safeNum(previous?.completedCount);
  const reopenDelta = safeNum(current?.reopenRatePercent) - safeNum(previous?.reopenRatePercent);
  const cycleDelta = avgCycleDays != null && prevAvgCycleDays != null ? avgCycleDays - prevAvgCycleDays : 0;

  const aThroughput = trendArrow(throughputDelta, false);
  const aReopen = trendArrow(reopenDelta, true);
  const aCycle = trendArrow(cycleDelta, true);
  const reopenHigh = safeNum(current?.reopenRatePercent) > 15;

  const statuses = useMemo(() => [...(board?.statuses ?? [])].sort((a, b) => a.order - b.order), [board]);

  const statusBreakdown = useMemo(
    () => statuses.map((s) => ({ name: s.name, count: tasks.filter((t) => t.status === s.id).length })),
    [statuses, tasks],
  );

  const priorityBreakdown = useMemo(() => {
    const counts: Record<'high' | 'medium' | 'low' | 'none', number> = { high: 0, medium: 0, low: 0, none: 0 };
    tasks.forEach((t) => {
      const key = t.priority ?? 'none';
      counts[key] += 1;
    });
    return counts;
  }, [tasks]);

  const remainingOpen = useMemo(() => {
    const doneStatusIds = new Set(statuses.filter((s) => s.category === 'done').map((s) => s.id));
    return tasks.filter((t) => !doneStatusIds.has(t.status)).length;
  }, [tasks, statuses]);

  const periodDays = useMemo(() => {
    if (!stat?.dateRange) return 30;
    const from = new Date(stat.dateRange.from).getTime();
    const to = new Date(stat.dateRange.to).getTime();
    const days = Math.round((to - from) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 30;
  }, [stat?.dateRange]);

  const throughputPerDay = current ? safeNum(current.completedCount) / periodDays : 0;
  const forecastDays = throughputPerDay > 0 ? remainingOpen / throughputPerDay : null;
  const forecastDate = forecastDays != null ? new Date(Date.now() + forecastDays * 24 * 60 * 60 * 1000) : null;

  const bottlenecksOption = useMemo(() => {
    const list = stat?.bottlenecks ?? [];
    const top = [...list].sort((a, b) => safeNum(b.avgDwellDays) - safeNum(a.avgDwellDays)).slice(0, 10);
    return {
      tooltip: {
        trigger: 'item',
        formatter: (p: any) =>
          `<b>${p.name}</b><br/>Avg dwell: ${fmt1(p.value)} days<br/>${top[p.dataIndex]?.taskCount ?? 0} task(s) here now`,
      },
      grid: { left: 140, right: 16, top: 10, bottom: 20 },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: top.map((x) => x.statusName), axisLabel: { width: 130, overflow: 'truncate' } },
      series: [
        { type: 'bar', data: top.map((x) => safeNum(x.avgDwellDays)), barWidth: 14, itemStyle: { borderRadius: 8 } },
      ],
    };
  }, [stat?.bottlenecks]);

  const statusBreakdownOption = useMemo(
    () => ({
      tooltip: { trigger: 'item' },
      grid: { left: 100, right: 16, top: 10, bottom: 20 },
      xAxis: { type: 'value' },
      yAxis: {
        type: 'category',
        data: statusBreakdown.map((s) => s.name),
        axisLabel: { width: 90, overflow: 'truncate' },
      },
      series: [
        { type: 'bar', data: statusBreakdown.map((s) => s.count), barWidth: 14, itemStyle: { borderRadius: 8 } },
      ],
    }),
    [statusBreakdown],
  );

  const priorityBreakdownOption = useMemo(
    () => ({
      tooltip: { trigger: 'item' },
      series: [
        {
          type: 'pie',
          radius: ['50%', '75%'],
          label: { formatter: (p: any) => `${p.name}\n${p.value}` },
          data: [
            { name: 'High', value: priorityBreakdown.high },
            { name: 'Medium', value: priorityBreakdown.medium },
            { name: 'Low', value: priorityBreakdown.low },
            { name: 'None', value: priorityBreakdown.none },
          ].filter((d) => d.value > 0),
          itemStyle: { borderRadius: 8, borderWidth: 2, borderColor: '#0000' },
        },
      ],
    }),
    [priorityBreakdown],
  );

  const periodComparisonOption = useMemo(() => {
    if (!current || !previous) return null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 40, right: 16, top: 16, bottom: 44, containLabel: true },
      xAxis: { type: 'category', data: ['Completed', 'Reopen %', 'Cycle (d)', 'On-time %'] },
      yAxis: { type: 'value' },
      series: [
        {
          name: 'Previous period',
          type: 'bar',
          data: [
            safeNum(previous.completedCount),
            fmt1(safeNum(previous.reopenRatePercent)),
            prevAvgCycleDays != null ? fmt1(prevAvgCycleDays) : 0,
            fmt1(safeNum(previous.deliveryAccuracyPercent)),
          ],
          barWidth: 18,
          itemStyle: { borderRadius: 6 },
        },
        {
          name: 'Current period',
          type: 'bar',
          data: [
            safeNum(current.completedCount),
            fmt1(safeNum(current.reopenRatePercent)),
            avgCycleDays != null ? fmt1(avgCycleDays) : 0,
            fmt1(safeNum(current.deliveryAccuracyPercent)),
          ],
          barWidth: 18,
          itemStyle: { borderRadius: 6 },
        },
      ],
    };
  }, [current, previous, avgCycleDays, prevAvgCycleDays]);

  const rangeLabel = stat?.dateRange
    ? `${new Date(stat.dateRange.from).toLocaleDateString()} → ${new Date(stat.dateRange.to).toLocaleDateString()}`
    : undefined;

  useEffect(() => {
    setDashHeader({
      component: 'project',
      breadcrumb: (
        <Link to="/dashboard/projects" className="btn btn-sm btn-ghost px-0 h-auto min-h-0">
          <Tooltip message="Projects" position="bottom">
            <span className="text-xl">←</span>
          </Tooltip>
        </Link>
      ),
      title: stat ? (
        <span className="flex items-center gap-2">
          {project?.name ?? 'Standalone project'}
          <span className={`badge ${psiBadge(psi).cls} text-sm`}>
            PSI {psi} · {psiBadge(psi).label}
          </span>
        </span>
      ) : (
        (project?.name ?? 'Standalone project')
      ),
      subtitle: (
        <span className="flex items-center gap-2">
          <span className="badge badge-sm badge-outline">Standalone</span>
          {rangeLabel && (
            <span>
              Range: <span className="font-medium">{rangeLabel}</span>
            </span>
          )}
        </span>
      ),
      actions: (
        <div className="flex items-center gap-2">
          <Link to={`/standalone/${projectId}`} className="btn btn-outline btn-sm">
            Open board →
          </Link>
          <button
            className="btn btn-primary btn-sm shadow-glow gap-1.5"
            onClick={() =>
              openChat({
                projectId,
                connectedAgents: ['StandaloneAgent'],
                suggestedPrompts: [
                  'What should I focus on next in this project?',
                  'Which tasks are overdue?',
                  ...(reopenHigh ? ['Why is the reopen rate high?'] : []),
                  ...(stat?.bottlenecks?.length ? ['Explain the main bottleneck'] : []),
                ],
                analyticsContext: {
                  scope: 'project',
                  projectIds: projectId ? [projectId] : undefined,
                  dateRange:
                    dateRangeCtx?.from && dateRangeCtx?.to
                      ? { from: asISODate(dateRangeCtx.from), to: asISODate(dateRangeCtx.to) }
                      : undefined,
                },
              })
            }
          >
            <LuBot size={14} />
            Ask mFlow
          </button>
          <ActionsMenu
            items={[
              { label: 'Knowledge', icon: LuBookOpenText, to: `/dashboard/projects/${projectId}/knowledge` },
              { label: 'Transfer…', icon: LuArrowRightLeft, onClick: () => setIsTransferModalOpen(true) },
            ]}
          />
        </div>
      ),
    });
    return () => setDashHeader({});
  }, [project?.name, projectId, stat, psi, rangeLabel, reopenHigh]);

  if (loading) return <StatLoading />;
  if (error) return <StatError message={error} />;
  if (!stat) return <StatEmpty message="No data available." />;

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard
            label={
              <>
                PSI
                <InfoTip
                  text={
                    'Project Stability Index (0–100)\n\n0.4 × delivery + 0.3 × quality + 0.3 × flow.\nHigher = more stable.'
                  }
                />
              </>
            }
          >
            <div className="grid grid-cols-2 gap-2 mt-2 items-center">
              <div className="text-3xl font-semibold">{psi}</div>
              <PsiGauge value={psi} />
            </div>
          </KpiCard>

          <KpiCard
            label={
              <>
                Throughput
                <InfoTip text={'Tasks that reached a done-category status in the selected range.'} />
              </>
            }
            footer="Completed in range"
          >
            <div className="flex items-end gap-2">
              <div className="text-3xl font-semibold">{safeNum(current?.completedCount)}</div>
              <div className={`text-sm ${aThroughput.cls}`}>
                {aThroughput.s} {Math.abs(throughputDelta)}
              </div>
            </div>
          </KpiCard>

          <KpiCard
            label={
              <>
                Avg cycle time
                <InfoTip text={'First in-progress transition → first done transition.\nLower is better.'} />
              </>
            }
            footer="Completed tasks"
          >
            <div className="flex items-end gap-2">
              <div className="text-2xl font-semibold">{avgCycleDays != null ? fmtDays(avgCycleDays) : '—'}</div>
              {avgCycleDays != null && prevAvgCycleDays != null && (
                <div className={`text-sm ${aCycle.cls}`}>
                  {aCycle.s} {fmtDays(Math.abs(cycleDelta))}
                </div>
              )}
            </div>
          </KpiCard>

          <KpiCard
            label={
              <>
                Reopen rate
                <InfoTip text={'Done → non-done transitions / completed tasks.\nLower is better.'} />
              </>
            }
            footer="Quality / clarity"
          >
            <div className="flex items-end gap-2">
              <div className={`text-3xl font-semibold ${reopenHigh ? 'text-error' : ''}`}>
                {fmtPct(safeNum(current?.reopenRatePercent))}
              </div>
              <div className={`text-sm ${aReopen.cls}`}>
                {aReopen.s} {fmt1(Math.abs(reopenDelta))}%
              </div>
            </div>
          </KpiCard>

          <KpiCard
            label={
              <>
                Delivery accuracy
                <InfoTip
                  text={
                    'Completed tasks finished on/before their due date.\nNeutral (100%) if nothing completed had a due date.'
                  }
                />
              </>
            }
            footer={`${safeNum(current?.completedWithDueCount)} with due date`}
          >
            <div className="text-3xl font-semibold">{fmtPct(safeNum(current?.deliveryAccuracyPercent))}</div>
          </KpiCard>

          <KpiCard
            label={
              <>
                Active load
                <InfoTip text={'Tasks currently in an in-progress status. Live snapshot, not range-scoped.'} />
              </>
            }
            footer="WIP now"
          >
            <div className="text-3xl font-semibold">{safeNum(stat.wipCount)}</div>
          </KpiCard>
        </div>

        {/* Forecast */}
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4 flex-row items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-lg font-semibold flex items-center">
                Forecast completion
                <InfoTip
                  text={
                    'Remaining open tasks ÷ average daily throughput in the selected range.\nA rough estimate — assumes no scope changes.'
                  }
                />
              </div>
              <div className="text-sm opacity-70">{remainingOpen} task(s) remaining open</div>
            </div>
            <div className="text-right">
              {forecastDate ? (
                <>
                  <div className="text-2xl font-semibold">{forecastDate.toLocaleDateString()}</div>
                  <div className="text-xs opacity-60">~{fmt1(throughputPerDay)} tasks/day pace</div>
                </>
              ) : (
                <div className="text-sm opacity-60">Not enough throughput data to forecast</div>
              )}
            </div>
          </div>
        </div>

        {/* Bottlenecks */}
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4">
            <div className="text-lg font-semibold flex items-center">
              Bottlenecks
              <InfoTip
                text={
                  'Current dwell time per status — how long tasks presently sitting there have been stuck, not a historical average.'
                }
              />
            </div>
            <div className="text-sm opacity-70">Where tasks are waiting right now</div>

            {stat.bottlenecks.length ? (
              <div className="h-[260px] mt-2">
                <ReactECharts option={bottlenecksOption} style={{ height: '100%' }} />
              </div>
            ) : (
              <div className="py-6 opacity-70">No tasks currently in flight.</div>
            )}
          </div>
        </div>

        {/* Period comparison — no daily series yet for Standalone, see InfoTip */}
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-4">
            <div className="text-lg font-semibold flex items-center">
              Current vs previous period
              <InfoTip
                text={
                  "Standalone doesn't have a daily-bucketed metrics series yet — this compares the selected range against the prior equal-length range instead of a day-by-day trend."
                }
              />
            </div>
            <div className="text-sm opacity-70">{rangeLabel ?? 'Last 30 days vs prior 30 days'}</div>

            {periodComparisonOption ? (
              <div className="h-[300px] mt-2">
                <ReactECharts option={periodComparisonOption} style={{ height: '100%' }} />
              </div>
            ) : (
              <div className="py-6 opacity-70">Not enough data.</div>
            )}
          </div>
        </div>

        {/* Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4">
              <div className="text-lg font-semibold">Tasks by status</div>
              <div className="text-sm opacity-70">Current board state</div>
              {boardLoading ? (
                <div className="py-6 flex justify-center">
                  <div className="loading loading-spinner" />
                </div>
              ) : statusBreakdown.length ? (
                <div className="h-[240px] mt-2">
                  <ReactECharts option={statusBreakdownOption} style={{ height: '100%' }} />
                </div>
              ) : (
                <div className="py-6 opacity-70">No statuses configured.</div>
              )}
            </div>
          </div>

          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4">
              <div className="text-lg font-semibold">Tasks by priority</div>
              <div className="text-sm opacity-70">Current board state</div>
              {boardLoading ? (
                <div className="py-6 flex justify-center">
                  <div className="loading loading-spinner" />
                </div>
              ) : tasks.length ? (
                <div className="h-[240px] mt-2">
                  <ReactECharts option={priorityBreakdownOption} style={{ height: '100%' }} />
                </div>
              ) : (
                <div className="py-6 opacity-70">No tasks yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isTransferModalOpen && projectId && (
        <TransferModal
          sourceProvider="standalone"
          sourceProjectId={projectId}
          sourceProjectName={project?.name}
          onClose={() => setIsTransferModalOpen(false)}
        />
      )}
    </div>
  );
}
